import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Usage } from '../types';
import { useAuthContext } from '../contexts/AuthContext';

const FREE_PLAN_LIMIT = 10;

export const useUsage = () => {
    const { user } = useAuthContext();
    const [usage, setUsage] = useState<Usage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const today = new Date().toISOString().split('T')[0];

    const fetchUsage = useCallback(async () => {
        if (!user) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const { data, error: fetchError } = await supabase
            .from('usage')
            .select('*')
            .eq('user_id', user.id)
            .eq('date', today)
            .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
            // PGRST116 = row not found, that's ok
            setError(fetchError.message);
        } else {
            setUsage(data as Usage | null);
        }
        setLoading(false);
    }, [user, today]);

    useEffect(() => {
        fetchUsage();
    }, [fetchUsage]);

    /**
     * Increment usage count for today. Creates a row if one doesn't exist yet.
     * Returns true if usage was successfully incremented, false if limit exceeded.
     */
    const incrementUsage = async (plan: string): Promise<boolean> => {
        if (!user) return false;

        const currentCount = usage?.count ?? 0;

        // Enforce free plan limit before writing
        if (plan === 'free' && currentCount >= FREE_PLAN_LIMIT) {
            return false;
        }

        if (usage) {
            // Update existing row
            const { error: updateError } = await supabase
                .from('usage')
                .update({ count: currentCount + 1 })
                .eq('id', usage.id);

            if (updateError) {
                setError(updateError.message);
                return false;
            }
            setUsage({ ...usage, count: currentCount + 1 });
        } else {
            // Insert new row for today
            const { data, error: insertError } = await supabase
                .from('usage')
                .insert({ user_id: user.id, date: today, count: 1 })
                .select()
                .single();

            if (insertError) {
                setError(insertError.message);
                return false;
            }
            setUsage(data as Usage);
        }
        return true;
    };

    const canGenerate = (plan: string): boolean => {
        if (plan !== 'free') return true;
        return (usage?.count ?? 0) < FREE_PLAN_LIMIT;
    };

    return {
        usage,
        loading,
        error,
        fetchUsage,
        incrementUsage,
        canGenerate,
        dailyLimit: FREE_PLAN_LIMIT,
    };
};
